import { useRef } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'

import useClickOutside from 'shared/hooks/useClickOutside'
import CloseButton from '../CloseButton'
import ErrorMessage from '@components/ErrorMessage'
import Button from '@components/Button'
import CircleLoader from '@components/CircleLoader'
import useBodyScrollLock from '../../hooks/useBodyScrollLock'
import useKeyPress from '../../hooks/useKeyPress'

import './styles.scss'

const withBackdrop = Component => props => {
    useBodyScrollLock()

    return createPortal(
        <div className="modal-dropdown">
            <Component {...props} />
        </div>,
        document.body
    )
}

const Modal = ({
    onClose,
    header,
    children,
    ComponentLoadingFallback,
    loading,
    className,
    buttons,
    size = 'small',
    Component,
    disabled,
    error,
    contentRef,
    onScroll,
    outsideClickDisabled,
    ...props
}) => {
    const ref = useRef()
    useClickOutside(ref, () => !outsideClickDisabled && onClose && onClose())

    const escapePress = useKeyPress('Escape')

    React.useEffect(() => {
        if (escapePress && onClose) {
            onClose()
        }
    }, [escapePress])

    return Component ? (
        <Component {...props} onClose={onClose} />
    ) : (
        <div
            tabIndex="-1"
            className={classNames('modal', {
                [`modal--${size}`]: size,
                [className]: className,
                [`modal--no-header-paddings`]: props.noHeaderPaddings,
            })}>
            <div className="modal__header">
                <h3 className="font-weight-bold">{header}</h3>
                {onClose && <CloseButton action={onClose} />}
            </div>
            <div className="modal__body" ref={contentRef} onScroll={onScroll}>
                {loading ? (
                    ComponentLoadingFallback ? (
                        <ComponentLoadingFallback />
                    ) : (
                        <CircleLoader className="align-self-center" size="small" />
                    )
                ) : (
                    children
                )}
            </div>
            {error && <ErrorMessage className="mb-3" messages={[error.message]} />}
            {buttons && (
                <div className="modal__footer">
                    {buttons.map((button, index) => (
                        <Button
                            key={index}
                            btnStyle={button.btnStyle}
                            className={button.btnClassName}
                            onClick={button.onClick}
                            disabled={disabled || loading || button.disabled}
                            size="small"
                            isLoading={button.loading}>
                            {button.label}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    )
}

Modal.propTypes = {
    header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    onClose: PropTypes.func,
    loading: PropTypes.bool,
    buttons: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            onClick: PropTypes.func.isRequired,
        })
    ),
    className: PropTypes.string,
    ComponentLoadingFallback: PropTypes.func,
    Component: PropTypes.func,
    size: PropTypes.oneOf(['small', 'large']),
}

export default withBackdrop(Modal)
